import React, { useEffect, useMemo, useState } from "react";
import {
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import useStudentPortalFetch, {
  StudentPortalShell,
  StudentPortalState,
} from "./useStudentPortalFetch";
import useStudentCourseSelection from "./useStudentCourseSelection";
import StudentCourseFilter from "./StudentCourseFilter";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function toYmd(input) {
  if (input == null) return "";
  const s = String(input).trim();
  if (!s) return "";
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return format(d, "yyyy-MM-dd");
}

function statusClass(status) {
  const s = String(status || "").toLowerCase();
  if (s.includes("present")) return "ems-student-badge-present";
  if (s.includes("absent")) return "ems-student-badge-absent";
  if (s.includes("late")) return "ems-student-badge-late";
  return "ems-student-badge-neutral";
}

function dayTone(status) {
  const s = String(status || "").toLowerCase();
  if (s.includes("present")) return "present";
  if (s.includes("absent")) return "absent";
  if (s.includes("late")) return "late";
  if (s) return "marked";
  return "";
}

function dayPrimaryStatus(list) {
  if (!list.length) return "";
  if (list.some((s) => String(s.attendance_status).toLowerCase().includes("absent"))) {
    return "Absent";
  }
  if (list.every((s) => String(s.attendance_status).toLowerCase().includes("present"))) {
    return "Present";
  }
  return list[0].attendance_status || "Marked";
}

function dayStatusAbbr(status) {
  const value = String(status || "").toLowerCase();
  if (value.includes("absent")) return "A";
  if (value.includes("late")) return "L";
  if (value.includes("present")) return "P";
  return String(status || "").slice(0, 1).toUpperCase() || "•";
}

export default function StudentAttendancePage() {
  const { data, loading, error } = useStudentPortalFetch("attendance");
  const {
    options,
    selectedAdmissionId,
    setSelectedAdmissionId,
    showFilter,
    loading: coursesLoading,
    filterRows,
  } = useStudentCourseSelection();
  const rows = filterRows(Array.isArray(data) ? data : []);

  const sessions = useMemo(
    () =>
      rows
        .map((row) => ({
          ...row,
          _ymd: toYmd(row.attendance_date || row.class_date),
        }))
        .filter((row) => row._ymd),
    [rows]
  );

  const [viewMode, setViewMode] = useState("month");
  const [cursor, setCursor] = useState(() => new Date());
  const [selectedYmd, setSelectedYmd] = useState(null);
  const [didInitCursor, setDidInitCursor] = useState(false);

  useEffect(() => {
    if (didInitCursor || !sessions.length) return;
    const latest = sessions.reduce((a, b) => (a._ymd > b._ymd ? a : b));
    try {
      setCursor(parseISO(`${latest._ymd}T12:00:00`));
      setSelectedYmd(latest._ymd);
    } catch {
      /* ignore */
    }
    setDidInitCursor(true);
  }, [sessions, didInitCursor]);

  const rangeDays = useMemo(() => {
    if (viewMode === "week") {
      return eachDayOfInterval({
        start: startOfWeek(cursor),
        end: endOfWeek(cursor),
      });
    }
    return eachDayOfInterval({
      start: startOfWeek(startOfMonth(cursor)),
      end: endOfWeek(endOfMonth(cursor)),
    });
  }, [cursor, viewMode]);

  const visibleRangeLabel = useMemo(() => {
    if (viewMode === "week") {
      return `${format(startOfWeek(cursor), "d MMM")} – ${format(
        endOfWeek(cursor),
        "d MMM yyyy"
      )}`;
    }
    return format(cursor, "MMMM yyyy");
  }, [cursor, viewMode]);

  const sessionsByYmd = useMemo(() => {
    const map = {};
    sessions.forEach((s) => {
      if (!map[s._ymd]) map[s._ymd] = [];
      map[s._ymd].push(s);
    });
    return map;
  }, [sessions]);

  const selectedDaySessions = selectedYmd
    ? sessionsByYmd[selectedYmd] || []
    : [];

  return (
    <StudentPortalShell
      className="ems-att-page"
      title="My Attendance"
      subtitle={
        sessions.length
          ? `${sessions.length} attendance record${
              sessions.length === 1 ? "" : "s"
            } for your batches.`
          : "Your class attendance for enrolled batches."
      }
      headerRight={
        <StudentCourseFilter
          options={options}
          value={selectedAdmissionId}
          onChange={setSelectedAdmissionId}
          showFilter={showFilter}
          loading={coursesLoading}
        />
      }
    >
      <StudentPortalState loading={loading} error={error}>
        <div className="ems-att-toolbar">
          <div className="ems-att-toolbar-left">
            <div className="ems-att-nav">
              <button
                type="button"
                aria-label="Previous"
                onClick={() =>
                  setCursor((d) =>
                    viewMode === "week" ? subWeeks(d, 1) : subMonths(d, 1)
                  )
                }
              >
                <ChevronLeft size={18} />
              </button>
              <strong>{visibleRangeLabel}</strong>
              <button
                type="button"
                aria-label="Next"
                onClick={() =>
                  setCursor((d) =>
                    viewMode === "week" ? addWeeks(d, 1) : addMonths(d, 1)
                  )
                }
              >
                <ChevronRight size={18} />
              </button>
            </div>
            <div className="ems-att-seg">
              <button
                type="button"
                className={viewMode === "month" ? "is-active" : ""}
                onClick={() => setViewMode("month")}
              >
                Month
              </button>
              <button
                type="button"
                className={viewMode === "week" ? "is-active" : ""}
                onClick={() => setViewMode("week")}
              >
                Week
              </button>
            </div>
          </div>
        </div>

        <div className="ems-att-cal">
          <div className="ems-att-cal-weekdays">
            {WEEKDAY_LABELS.map((d) => (
              <div key={d}>
                <span className="ems-att-wd-full">{d}</span>
                <span className="ems-att-wd-short">{d.slice(0, 1)}</span>
              </div>
            ))}
          </div>
          <div className="ems-att-cal-grid">
            {rangeDays.map((day) => {
              const ymd = format(day, "yyyy-MM-dd");
              const inMonth =
                viewMode === "week" || isSameMonth(day, cursor);
              if (viewMode === "month" && !inMonth) {
                return <div key={ymd} className="ems-att-day is-pad" />;
              }
              const list = sessionsByYmd[ymd] || [];
              const has = list.length > 0;
              const att = dayPrimaryStatus(list);
              const isSelected = selectedYmd === ymd;
              const isToday = isSameDay(day, new Date());
              return (
                <button
                  key={ymd}
                  type="button"
                  className={[
                    "ems-att-day",
                    has ? `is-${dayTone(att)}` : "",
                    isSelected ? "is-selected" : "",
                    viewMode === "week" ? "is-week" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => setSelectedYmd(has ? ymd : null)}
                >
                  <span className={`ems-att-day-num${isToday ? " is-today" : ""}`}>
                    {format(day, "d")}
                  </span>
                  {has
                    ? list.slice(0, 2).map((s) => (
                        <span
                          key={s.organization_ems_class_attendance_id}
                          className="ems-att-day-chip"
                          title={`${s.topic || "Class"} · ${s.attendance_status}`}
                        >
                          {String(s.topic || "Class").slice(0, 22)}
                        </span>
                      ))
                    : null}
                  {has ? (
                    <span className="ems-att-day-status">
                      <span className="ems-att-status-full">{att}</span>
                      <span className="ems-att-status-short">{dayStatusAbbr(att)}</span>
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        <div className="ems-att-legend">
          <span>Attendance:</span>
          <i className="is-present" /> Present
          <i className="is-absent" /> Absent
          <i className="is-late" /> Late
        </div>

        {selectedYmd ? (
          <section className="ems-student-section">
            <h3 className="ems-student-section-title">
              {format(parseISO(`${selectedYmd}T12:00:00`), "d MMM yyyy")}
            </h3>
            <div className="ems-student-table-wrap">
              <table className="ems-student-table">
                <thead>
                  <tr>
                    <th>Topic</th>
                    <th>Batch</th>
                    <th>Status</th>
                    <th>Duration</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedDaySessions.map((row) => (
                    <tr key={row.organization_ems_class_attendance_id}>
                      <td>
                        <div className="ems-student-program-name">
                          <span>{row.topic || "Class"}</span>
                        </div>
                        {row.training_program_name ? (
                          <p className="ems-student-meta">
                            {row.training_program_name}
                          </p>
                        ) : null}
                      </td>
                      <td>{row.batch_name || "—"}</td>
                      <td>
                        <span
                          className={`ems-student-badge ${statusClass(
                            row.attendance_status
                          )}`}
                        >
                          {row.attendance_status || "—"}
                        </span>
                      </td>
                      <td>{row.duration || "—"}</td>
                      <td>{row.remarks || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : (
          <p className="ems-student-meta">
            Click a day with attendance to see details.
          </p>
        )}
      </StudentPortalState>
    </StudentPortalShell>
  );
}
